import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs, {Dayjs} from 'dayjs';
import {type FC, useState} from 'react';
import {useTranslation} from 'react-i18next';
import DialogPanel from '@/components/common/DialogPanel';
import CalendarPicker from '@/components/common/forms/CalendarPicker';
import InputText from '@/components/common/forms/InputText';
import Select from '@/components/common/forms/Select';
import Label from '@/components/common/Label';
import type {LidarTechnology} from '@/domain/entities/LidarTechnology';
import type {SlamPointCloudCreate} from '@/domain/entities/SlamPointCloud';

//TYPES
type Props = {
  lidarTechnologies?: LidarTechnology[];
  onUploadPointCloud?: (pointCloud: Partial<SlamPointCloudCreate>, file: File) => void;
  onClose: () => void;
};

const SlamPointCloudUploadingDialog: FC<Props> = ({lidarTechnologies, onUploadPointCloud, onClose}) => {
  const {t} = useTranslation();

  const initialPointCloud: Partial<SlamPointCloudCreate> = {date: dayjs()};
  const [partialEntity, setPartialEntity] = useState<Partial<SlamPointCloudCreate>>(initialPointCloud);
  const [file, setFile] = useState<File>();

  const handleDate = (date?: Dayjs) => date && setPartialEntity({...partialEntity, date});
  const handleLidarTechnology = (optionId?: string) =>
    setPartialEntity({...partialEntity, lidarTechnologyUuid: optionId});
  const handleComments = (comments: string) => setPartialEntity({...partialEntity, comments});

  const handleUpload = () => {
    if (file && partialEntity && onUploadPointCloud) {
      onUploadPointCloud(partialEntity as SlamPointCloudCreate, file);
    }
  };

  const hasAllPointCloudsProperties = partialEntity.date && partialEntity.lidarTechnologyUuid;

  const isUploadEnabled = hasAllPointCloudsProperties && file;

  return (
    <DialogPanel isOpen={true} label={t('pointClouds.title')} onClose={onClose}>
      <Stack direction="column" spacing={2} sx={{maxWidth: '350px', p: 1}}>
        <Typography>{t('pointClouds.helperText')}</Typography>
        <Label value={t('pointClouds.date')} required={true}>
          <CalendarPicker value={partialEntity ? partialEntity.date : new Dayjs()} onChange={handleDate} />
        </Label>
        <Label value={t('pointClouds.lidarTechnology')} required={true}>
          <Select
            options={lidarTechnologies ? lidarTechnologies : []}
            selectedOptionId={partialEntity?.lidarTechnologyUuid}
            onChange={handleLidarTechnology}
          />
        </Label>
        <Label value={t('pointClouds.comments')}>
          <InputText value={partialEntity?.comments} required={true} onChange={handleComments} multiline={true} />
        </Label>
        <Button component="label" variant="outlined" onClick={() => {}}>
          {file?.name || t('pointClouds.file')}
          <input type="file" accept=".laz,.las" onChange={(event) => setFile(event.target.files?.[0])} hidden />
        </Button>
        <Button variant="outlined" disabled={!isUploadEnabled} onClick={handleUpload}>
          {t('pointClouds.upload')}
        </Button>
      </Stack>
    </DialogPanel>
  );
};
export default SlamPointCloudUploadingDialog;
